import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useEffect } from "react";

/**
 * Breadcrumb component — renders a visual clickable trail + injects
 * BreadcrumbList JSON-LD schema for Google rich snippets.
 *
 * @param {Array<{label: string, to?: string}>} items - last item is the current page (no link)
 */
export default function Breadcrumb({ items }) {
  useEffect(() => {
    const baseUrl = "https://terpeneatlas.org";
    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: item.label,
        ...(item.to ? { item: `${baseUrl}${item.to}` } : {}),
      })),
    };

    let script = document.getElementById("breadcrumb-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.id = "breadcrumb-jsonld";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    return () => {
      const existing = document.getElementById("breadcrumb-jsonld");
      if (existing) existing.remove();
    };
  }, [items]);

  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex items-center flex-wrap gap-1 text-xs text-muted-foreground">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground/60" />}
            {item.to && i < items.length - 1 ? (
              <Link to={item.to} className="hover:text-foreground transition-colors">
                {item.label}
              </Link>
            ) : (
              <span className="text-foreground font-medium truncate max-w-[180px]">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}